import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import { notify } from '../../utils/notifications';
import useAuthStore from '../../store/authStore';
import {
  Settings, User, Lock, Shield, Bell, Mail, Database, 
  Save, Eye, EyeOff, Key, Smartphone, Globe, Palette
} from 'lucide-react';

const Impostazioni = () => {
  const { utente } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profilo');
  const [loading, setLoading] = useState(false);
  
  // Profilo
  const [profiloData, setProfiloData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    telefono: '',
  });

  // Sicurezza
  const [passwordData, setPasswordData] = useState({
    vecchia_password: '',
    nuova_password: '',
    conferma_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    vecchia: false,
    nuova: false,
    conferma: false,
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // Notifiche
  const [notificheSettings, setNotificheSettings] = useState({
    email_abbonamenti: true,
    email_pagamenti: true,
    email_corse: false,
    push_enabled: false,
  });

  // Sistema
  const [sistemaSettings, setSistemaSettings] = useState({
    nome_azienda: 'Casella Noleggio Bus',
    email_azienda: 'info@casellanoleggiobus.it',
    telefono_azienda: '+39 123 456 7890',
    indirizzo_azienda: 'Via Roma 1, 00100 Roma',
    colore_primario: '#667eea',
  });

  useEffect(() => {
    caricaImpostazioni();
  }, []);

  const caricaImpostazioni = async () => {
    try {
      setLoading(true);
      
      // Carica profilo utente corrente
      const profiloRes = await api.get('/autenticazione/utenti/profilo/');
      setProfiloData({
        first_name: profiloRes.data.first_name || '',
        last_name: profiloRes.data.last_name || '',
        email: profiloRes.data.email || '',
        telefono: profiloRes.data.telefono || '',
      });
      setTwoFactorEnabled(profiloRes.data.richiede_2fa || false);

    } catch (error) {
      console.error('Errore caricamento impostazioni:', error);
      notify.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfilo = async (e) => {
    e.preventDefault();
    const toastId = notify.loading('Salvataggio profilo...');
    
    try {
      await api.patch(`/autenticazione/utenti/${utente.id}/`, profiloData);
      notify.update(toastId, { type: 'success', render: 'Profilo aggiornato con successo!' });
      await caricaImpostazioni();
    } catch (error) {
      console.error('Errore salvataggio profilo:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nel salvataggio del profilo' });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.nuova_password !== passwordData.conferma_password) {
      notify.error('Le password non corrispondono');
      return;
    }

    if (passwordData.nuova_password.length < 8) {
      notify.error('La password deve essere di almeno 8 caratteri');
      return;
    }

    const toastId = notify.loading('Cambio password...');
    
    try {
      await api.post('/autenticazione/utenti/cambio_password/', {
        vecchia_password: passwordData.vecchia_password,
        nuova_password: passwordData.nuova_password,
      });
      
      notify.update(toastId, { type: 'success', render: 'Password cambiata con successo!' });
      setPasswordData({
        vecchia_password: '',
        nuova_password: '',
        conferma_password: '',
      });
    } catch (error) {
      console.error('Errore cambio password:', error);
      const errorMsg = error.response?.data?.vecchia_password?.[0] || 
                       error.response?.data?.detail || 
                       'Errore nel cambio password';
      notify.update(toastId, { type: 'error', render: errorMsg });
    }
  };

  const handleToggle2FA = async () => {
    const toastId = notify.loading(twoFactorEnabled ? 'Disattivazione 2FA...' : 'Attivazione 2FA...');
    
    try {
      await api.patch(`/autenticazione/utenti/${utente.id}/`, { 
        richiede_2fa: !twoFactorEnabled 
      });
      
      setTwoFactorEnabled(!twoFactorEnabled);
      notify.update(toastId, { 
        type: 'success', 
        render: twoFactorEnabled ? '2FA disattivato!' : '2FA attivato!' 
      });
    } catch (error) {
      console.error('Errore toggle 2FA:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nell\'operazione' });
    }
  };

  const handleSaveNotifiche = () => {
    notify.success('Impostazioni notifiche salvate!');
  };

  const handleSaveSistema = () => {
    notify.success('Impostazioni sistema salvate!');
  };

  const handleExportDatabase = () => {
    notify.info('Funzionalità export database in arrivo!');
  };

  const handleImportDatabase = () => {
    notify.info('Funzionalità import database in arrivo!');
  };

  const tabs = [
    { id: 'profilo', label: 'Profilo', icon: User },
    { id: 'sicurezza', label: 'Sicurezza', icon: Shield },
    { id: 'notifiche', label: 'Notifiche', icon: Bell },
    { id: 'sistema', label: 'Sistema', icon: Settings },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-gray-900">Impostazioni</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gestisci le tue preferenze e le configurazioni del sistema</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Sidebar Tabs */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="font-semibold">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            {/* TAB PROFILO */}
            {activeTab === 'profilo' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Informazioni Profilo
                </h2>

                <form onSubmit={handleSaveProfilo} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nome</label>
                      <input
                        type="text"
                        value={profiloData.first_name}
                        onChange={(e) => setProfiloData({ ...profiloData, first_name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Mario"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Cognome</label>
                      <input
                        type="text"
                        value={profiloData.last_name}
                        onChange={(e) => setProfiloData({ ...profiloData, last_name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Rossi"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={profiloData.email}
                      onChange={(e) => setProfiloData({ ...profiloData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="mario.rossi@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Telefono</label>
                    <input
                      type="tel"
                      value={profiloData.telefono}
                      onChange={(e) => setProfiloData({ ...profiloData, telefono: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+39 123 456 7890"
                    />
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold"
                    >
                      <Save className="w-4 h-4" />
                      Salva Modifiche
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* TAB SICUREZZA */}
            {activeTab === 'sicurezza' && (
              <div className="space-y-6">
                {/* Cambio Password */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-red-600" />
                    Cambio Password
                  </h2>

                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Password Attuale</label>
                      <div className="relative">
                        <input
                          type={showPasswords.vecchia ? 'text' : 'password'}
                          value={passwordData.vecchia_password}
                          onChange={(e) => setPasswordData({ ...passwordData, vecchia_password: e.target.value })}
                          className="w-full px-4 py-2 pr-12 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, vecchia: !showPasswords.vecchia })}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords.vecchia ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nuova Password</label>
                      <div className="relative">
                        <input
                          type={showPasswords.nuova ? 'text' : 'password'}
                          value={passwordData.nuova_password}
                          onChange={(e) => setPasswordData({ ...passwordData, nuova_password: e.target.value })}
                          className="w-full px-4 py-2 pr-12 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, nuova: !showPasswords.nuova })}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords.nuova ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Minimo 8 caratteri</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Conferma Nuova Password</label>
                      <div className="relative">
                        <input
                          type={showPasswords.conferma ? 'text' : 'password'}
                          value={passwordData.conferma_password}
                          onChange={(e) => setPasswordData({ ...passwordData, conferma_password: e.target.value })}
                          className="w-full px-4 py-2 pr-12 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, conferma: !showPasswords.conferma })}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords.conferma ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                      <button
                        type="submit"
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
                      >
                        <Key className="w-4 h-4" />
                        Cambia Password
                      </button>
                    </div>
                  </form>
                </div>

                {/* Autenticazione a Due Fattori */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-purple-600" />
                    Autenticazione a Due Fattori (2FA)
                  </h2>
                  <p className="text-gray-600 mb-5">
                    Aggiungi un ulteriore livello di sicurezza richiedendo un secondo metodo di autenticazione.
                  </p>

                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Shield className={`w-8 h-8 ${twoFactorEnabled ? 'text-purple-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="font-semibold text-gray-900">
                          {twoFactorEnabled ? '2FA Attivo' : '2FA Non Attivo'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {twoFactorEnabled 
                            ? 'Il tuo account è protetto con 2FA' 
                            : 'Attiva 2FA per maggiore sicurezza'
                          }
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleToggle2FA}
                      className={`px-6 py-2 rounded-lg font-semibold transition ${
                        twoFactorEnabled
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}
                    >
                      {twoFactorEnabled ? 'Disattiva' : 'Attiva'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* TAB NOTIFICHE */}
            {activeTab === 'notifiche' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-yellow-600" />
                  Preferenze Notifiche
                </h2>

                <div className="space-y-6">
                  {/* Email Notifications */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-600" />
                      Notifiche Email
                    </h3>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                        <div>
                          <p className="font-semibold text-gray-900">Nuovi Abbonamenti</p>
                          <p className="text-sm text-gray-600">Ricevi email quando viene creato un nuovo abbonamento</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={notificheSettings.email_abbonamenti}
                          onChange={(e) => setNotificheSettings({ ...notificheSettings, email_abbonamenti: e.target.checked })}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </label>

                      <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                        <div>
                          <p className="font-semibold text-gray-900">Pagamenti Ricevuti</p>
                          <p className="text-sm text-gray-600">Ricevi email quando viene completato un pagamento</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={notificheSettings.email_pagamenti}
                          onChange={(e) => setNotificheSettings({ ...notificheSettings, email_pagamenti: e.target.checked })}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </label>

                      <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                        <div>
                          <p className="font-semibold text-gray-900">Corse Completate</p>
                          <p className="text-sm text-gray-600">Ricevi email quando una corsa viene completata</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={notificheSettings.email_corse}
                          onChange={(e) => setNotificheSettings({ ...notificheSettings, email_corse: e.target.checked })}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Push Notifications */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-green-600" />
                      Notifiche Push
                    </h3>
                    <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                      <div>
                        <p className="font-semibold text-gray-900">Abilita Notifiche Push</p>
                        <p className="text-sm text-gray-600">Ricevi notifiche push per eventi importanti</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={notificheSettings.push_enabled}
                        onChange={(e) => setNotificheSettings({ ...notificheSettings, push_enabled: e.target.checked })}
                        className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                      />
                    </label>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSaveNotifiche}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold"
                    >
                      <Save className="w-4 h-4" />
                      Salva Preferenze
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* TAB SISTEMA */}
            {activeTab === 'sistema' && (
              <div className="space-y-6">
                {/* Informazioni Azienda */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-green-600" />
                    Informazioni Azienda
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Nome Azienda</label>
                      <input
                        type="text"
                        value={sistemaSettings.nome_azienda}
                        onChange={(e) => setSistemaSettings({ ...sistemaSettings, nome_azienda: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email Azienda</label>
                        <input
                          type="email"
                          value={sistemaSettings.email_azienda}
                          onChange={(e) => setSistemaSettings({ ...sistemaSettings, email_azienda: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Telefono Azienda</label>
                        <input
                          type="tel"
                          value={sistemaSettings.telefono_azienda}
                          onChange={(e) => setSistemaSettings({ ...sistemaSettings, telefono_azienda: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Indirizzo Azienda</label>
                      <input
                        type="text"
                        value={sistemaSettings.indirizzo_azienda}
                        onChange={(e) => setSistemaSettings({ ...sistemaSettings, indirizzo_azienda: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Colore Primario</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={sistemaSettings.colore_primario}
                          onChange={(e) => setSistemaSettings({ ...sistemaSettings, colore_primario: e.target.value })}
                          className="h-12 w-24 border border-gray-200 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={sistemaSettings.colore_primario}
                          onChange={(e) => setSistemaSettings({ ...sistemaSettings, colore_primario: e.target.value })}
                          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200 mt-6">
                    <button
                      onClick={handleSaveSistema}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold"
                    >
                      <Save className="w-4 h-4" />
                      Salva Configurazioni
                    </button>
                  </div>
                </div>

                {/* Database */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                    <Database className="w-5 h-5 text-blue-600" />
                    Gestione Database
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      onClick={handleExportDatabase}
                      className="flex flex-col items-center justify-center p-6 border border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition"
                    >
                      <Database className="w-12 h-12 text-blue-600 mb-3" />
                      <p className="font-bold text-gray-900 mb-1">Export Database</p>
                      <p className="text-sm text-gray-600 text-center">Scarica backup completo del database</p>
                    </button>

                    <button
                      onClick={handleImportDatabase}
                      className="flex flex-col items-center justify-center p-6 border border-purple-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition"
                    >
                      <Database className="w-12 h-12 text-purple-600 mb-3" />
                      <p className="font-bold text-gray-900 mb-1">Import Database</p>
                      <p className="text-sm text-gray-600 text-center">Ripristina database da backup</p>
                    </button>
                  </div>

                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Attenzione:</strong> Le operazioni sul database possono influenzare il funzionamento del sistema. 
                      Esegui sempre un backup prima di importare dati.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Impostazioni;

